import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import axios from 'axios';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { useOrgProject } from '@/context/OrgProjectContext/useOrgProject.ts';
import { useQuery } from '@tanstack/react-query';
import {
  useCompositionMutations,
  useCompositionRecord,
  useCompositionReadiness,
  useCompositionScope,
  useCallableContext,
} from '@/hooks/api/automations/useExecutionCompositions.ts';
import type { SuiteDetail, SuiteMember } from '@/types/automationSuites.ts';
import type {
  ExactVersion,
  Identity,
  Location,
  PrepareRequest,
  Preview,
  SemanticEdit,
} from '@/types/executionCompositions.ts';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';
import { getForgeFailureMessage } from '@/services/forgeFailureRouting.ts';
import {
  bindingOccurrences,
  chooseDependency,
  matchingInheritedCall,
  locationKey,
  locationWithin,
  preparationFromRecord,
  proposalChanged,
  proofKindLabel,
  outcomeLabel,
} from './compositionPresentation.ts';
import {
  ExactDetails,
  ImplementationLabel,
  LocationLabel,
  ReleaseSection,
} from './CompositionPresentation.tsx';
import { ImplementationSelect, InitialCalls, RootTriggers } from './CompositionSelectors.tsx';
import RequirementEditor from './RequirementEditor.tsx';
import ImplementationEditor from './ImplementationEditor.tsx';
import ReleaseWorkflowProgress from './ReleaseWorkflowProgress.tsx';
import SavedReleaseWorkflow, { SavedReleaseReview } from './SavedReleaseWorkflow.tsx';
import ReleaseSelectionReview from './ReleaseSelectionReview.tsx';

type Props = {
  releaseId?: string;
  rootVersionId?: string;
  suite?: SuiteDetail;
  mode?: 'advanced' | 'guided';
  canEdit: boolean;
  onSaved?: (releaseId: string) => void;
  semanticEdit?: SemanticEdit;
};
type PendingApply = {
  preview_token: string;
  preview_digest: string;
  request_id: string;
  expected_identity: Identity;
};
function readPending(key: string): PendingApply | null {
  try {
    const data = JSON.parse(sessionStorage.getItem(key) ?? 'null') as PendingApply | null;
    return data &&
      typeof data.request_id === 'string' &&
      typeof data.preview_token === 'string' &&
      typeof data.preview_digest === 'string' &&
      data.expected_identity
      ? data
      : null;
  } catch {
    return null;
  }
}
function retain(key: string, value: PendingApply | null) {
  try {
    if (value) sessionStorage.setItem(key, JSON.stringify(value));
    else sessionStorage.removeItem(key);
  } catch {
    /* Same-mount recovery remains available. */
  }
}

export default function CompositionWorkspace(props: Props) {
  const scope = useCompositionScope();
  return (
    <WorkspaceSession
      key={JSON.stringify([
        ...scope,
        props.releaseId,
        props.rootVersionId,
        props.suite?.id,
        props.mode ?? 'advanced',
      ])}
      {...props}
      recoveryKey={JSON.stringify([
        ...scope,
        'apply',
        props.releaseId,
        props.rootVersionId,
        props.suite?.id,
        props.mode ?? 'advanced',
      ])}
    />
  );
}
function WorkspaceSession({
  releaseId,
  rootVersionId,
  suite,
  mode = 'advanced',
  canEdit,
  onSaved,
  recoveryKey,
  semanticEdit,
}: Props & { recoveryKey: string }) {
  const api = useApiClient();
  const scope = useCompositionScope();
  const { projectId } = useOrgProject();
  const record = useCompositionRecord(releaseId);
  const readiness = useCompositionReadiness(releaseId);
  const source = useCallableContext(rootVersionId);
  const { prepare, apply, register } = useCompositionMutations();
  const [request, setRequest] = useState<PrepareRequest>({
    project_id: projectId!,
    roots: [],
    selections: [],
    initial_selections: [],
    requirements: [],
    ...(suite ? { suite_id: suite.id, expected_suite_head_revision: suite.head_revision } : {}),
  });
  const [preview, setPreview] = useState<Preview | null>(null);
  const [proposalBasis, setProposalBasis] = useState<PrepareRequest>();
  const [pending, setPending] = useState<PendingApply | null>(() => readPending(recoveryKey));
  const [savedId, setSavedId] = useState<string>();
  const [error, setError] = useState('');
  const [created, setCreated] = useState<ExactVersion[] | null>(null);
  const [versions, setVersions] = useState<Record<string, string>>({});
  const [replace, setReplace] = useState(false);
  const [activeStage, setActiveStage] = useState<1 | 2>(() => (releaseId ? 2 : 1));
  const [editVersionId, setEditVersionId] = useState<string | null>(null);
  const [edit, setEdit] = useState<SemanticEdit | undefined>(semanticEdit);
  const mounted = useRef(true);
  const inFlight = useRef(false);
  const initialized = useRef(false);
  const guidedRootsInitialized = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const members = useQuery({
    queryKey: [...scope, 'members', suite?.id, suite?.head_revision],
    enabled: Boolean(suite),
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async ({ signal }) => {
      const items: SuiteMember[] = [];
      let page = 1;
      for (;;) {
        const { data } = await api.get<SuiteDetail>(`/automation-suites/${suite!.id}`, {
          signal,
          params: { page, page_size: 50 },
        });
        if (
          data.id !== suite!.id ||
          data.project_id !== projectId ||
          data.head_revision !== suite!.head_revision ||
          !data.shared_text_available ||
          data.archived_at
        )
          throw new Error('Suite membership or authority changed. Refresh before preparing.');
        items.push(...data.members.results);
        if (!data.members.next) break;
        if (!Number.isInteger(data.members.next.page) || data.members.next.page <= page)
          throw new Error('Incomplete Suite membership discovery.');
        page = data.members.next.page;
      }
      return items.filter((item) => item.availability === 'active');
    },
  });
  useEffect(() => {
    if (record.data && record.isFetchedAfterMount && !record.isError && !initialized.current) {
      try {
        const basis = preparationFromRecord(record.data);
        setRequest(basis);
        setProposalBasis(basis);
        initialized.current = true;
      } catch (cause) {
        setError((cause as Error).message);
      }
    }
  }, [record.data, record.isFetchedAfterMount, record.isError]);
  useEffect(() => {
    if (
      mode !== 'guided' ||
      releaseId ||
      !rootVersionId ||
      !source.data ||
      !source.isFetchedAfterMount ||
      guidedRootsInitialized.current
    )
      return;
    guidedRootsInitialized.current = true;
    setRequest((current) => ({
      ...current,
      roots: source.data!.triggers.map((trigger) => ({
        automation_id: source.data!.automation_id,
        automation_version_id: rootVersionId,
        trigger_step_id: trigger.step_id,
        role: 'entrypoint',
      })),
    }));
  }, [mode, releaseId, rootVersionId, source.data, source.isFetchedAfterMount]);
  const busy = prepare.isPending || apply.isPending || register.isPending;
  const staleBase = Boolean(
    request.base &&
    record.data &&
    (request.base.lifecycle_revision !== record.data.record.lifecycle_revision ||
      Object.entries(request.base.identity).some(
        ([key, value]) => record.data!.record.identity[key as keyof Identity] !== value,
      )),
  );
  const suiteUnavailable = Boolean(
    suite &&
    (!members.isFetchedAfterMount ||
      members.isError ||
      request.expected_suite_head_revision !== suite.head_revision),
  );
  const sourceUnavailable = Boolean(!releaseId && rootVersionId && source.isError);
  const sourceDenied =
    axios.isAxiosError(source.error) &&
    (source.error.response?.status === 401 || source.error.response?.status === 403);
  const locked = busy || Boolean(pending) || !canEdit || Boolean(savedId) || sourceUnavailable;
  const expiry = preview ? Date.parse(preview.intent.expires_at) : NaN;
  const expired = !Number.isFinite(expiry) || expiry <= Date.now();
  const [, tick] = useState(0);
  useEffect(() => {
    if (!preview) return;
    const timer = setTimeout(
      () => tick((value) => value + 1),
      Math.max(0, Date.parse(preview.intent.expires_at) - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [preview]);
  const change = (next: PrepareRequest) => {
    if (locked) return;
    setRequest(next);
    setPreview(null);
    setError('');
  };
  const inspection = useMemo(() => {
    try {
      return {
        occurrences: record.data ? bindingOccurrences(record.data.candidate) : [],
        error: '',
      };
    } catch (cause) {
      return { occurrences: [], error: (cause as Error).message };
    }
  }, [record.data]);
  const occurrences = inspection.occurrences;
  const visibleOccurrences = occurrences.filter(
    (item) =>
      !request.initial_selections.some((choice) =>
        locationWithin(item.location, choice.location),
      ) &&
      !request.selections.some(
        (choice) =>
          locationWithin(item.location, choice.location) &&
          locationKey(item.location) !== locationKey(choice.location),
      ),
  );
  const selectedEditVersion =
    editVersionId ?? rootVersionId ?? request.roots[0]?.automation_version_id;
  const selectedEditRoot = request.roots.find(
    (root) => root.automation_version_id === selectedEditVersion,
  );
  const discardEdit = () => {
    const paths =
      edit?.operations.filter(
        (operation) => operation.op_type === 'replace_subautomation_target',
      ) ?? [];
    const introduced = (location: Location) =>
      location.root.automation_version_id === edit?.base_version_id &&
      paths.some(
        (operation) =>
          location.call_chain[0]?.at(-1)?.step_id === operation.step_id &&
          (location.call_chain[0]?.at(-1)?.subflow_id ?? undefined) === operation.subflow_id,
      );
    setRequest((current) => ({
      ...current,
      initial_selections: current.initial_selections.filter(
        (choice) => !introduced(choice.location),
      ),
      selections: current.selections.filter((choice) => !introduced(choice.location)),
      requirements: current.requirements.filter((item) => !introduced(item.evidence_location)),
    }));
    setEdit(undefined);
    setPreview(null);
    setError('');
  };
  const inherited = new Map(
    occurrences.map((item) => [
      locationKey(item.location),
      {
        implementation: record.data!.candidate.nodes.find(
          (node) => node.binding_id === item.bindingId,
        )!,
        call: record.data!.candidate.edges[item.edgeIndex].call,
      },
    ]),
  );
  const chooseInitial = (
    location: Location,
    automationId: string,
    versionId: string | null,
    previous?: ExactVersion,
  ) => {
    change(
      chooseDependency(
        request,
        location,
        versionId ? { automation_id: automationId, automation_version_id: versionId } : null,
        previous,
      ),
    );
  };
  const chooseRoots = (automationId: string, roots: PrepareRequest['roots']) =>
    change({
      ...request,
      roots: [...request.roots.filter((root) => root.automation_id !== automationId), ...roots],
      initial_selections: request.initial_selections.filter(
        (item) => item.location.root.automation_id !== automationId,
      ),
      requirements: request.requirements.filter(
        (item) => item.evidence_location.root.automation_id !== automationId,
      ),
    });
  const prepareCandidate = async () => {
    if (inFlight.current || locked || staleBase || suiteUnavailable || !request.roots.length)
      return;
    inFlight.current = true;
    setError('');
    setPreview(null);
    try {
      let intent = {
        ...request,
        ...(replace && record.data ? { replace_draft: record.data.record } : {}),
      };
      if (edit) {
        if (
          intent.requirements.some(
            (item) => item.declaration.evidence.automation_version_id === edit.base_version_id,
          )
        )
          throw new Error(
            'The edited version needs reviewed successor-bound requirements. Remove stale declarations before compiling; cloned cases do not carry passing evidence.',
          );
        const registered = await register.mutateAsync(edit);
        // Explicitly authored successors are allocated by the server. Caller versions
        // are unchanged for ordinary dependency choices.
        const successor = registered.proposed_edits[0];
        if (!successor || registered.proposed_edits.length !== 1)
          throw new Error('Could not confirm the selected implementation edit.');
        const remapLocation = (location: Location): Location => ({
          ...location,
          root:
            location.root.automation_version_id === successor.source.automation_version_id
              ? {
                  ...location.root,
                  automation_version_id: successor.candidate.automation_version_id,
                }
              : location.root,
        });
        intent = {
          ...intent,
          implementation_edit_preview_ref: registered.reference,
          roots: intent.roots.map((root) =>
            root.automation_version_id === successor.source.automation_version_id
              ? { ...root, automation_version_id: successor.candidate.automation_version_id }
              : root,
          ),
          initial_selections: intent.initial_selections.map((item) => ({
            ...item,
            location: remapLocation(item.location),
          })),
          selections: intent.selections.map((item) => ({
            ...item,
            location: remapLocation(item.location),
          })),
        };
        intent.requirements = intent.requirements.map((item) => ({
          ...item,
          evidence_location: remapLocation(item.evidence_location),
          declaration: {
            ...item.declaration,
            entrypoint: {
              ...item.declaration.entrypoint,
              automation_version_id:
                item.declaration.entrypoint.automation_version_id ===
                successor.source.automation_version_id
                  ? successor.candidate.automation_version_id
                  : item.declaration.entrypoint.automation_version_id,
            },
          },
        }));
      }
      const result = await prepare.mutateAsync(intent);
      if (mounted.current) setPreview(result);
    } catch (cause) {
      if (mounted.current) {
        if (axios.isAxiosError(cause) && cause.response?.status === 403) discardEdit();
        setError(getForgeFailureMessage(cause) || getHttpErrorMessage(cause) || (cause as Error).message);
      }
    } finally {
      inFlight.current = false;
    }
  };
  const save = async () => {
    if (
      inFlight.current ||
      !canEdit ||
      sourceUnavailable ||
      (!pending &&
        (staleBase || suiteUnavailable || !preview || preview.intent.status !== 'ready' || expired))
    )
      return;
    const submission = pending ?? {
      preview_token: preview!.preview_token,
      preview_digest: preview!.preview_digest,
      request_id: crypto.randomUUID(),
      expected_identity: preview!.intent.candidate_identity!,
    };
    retain(recoveryKey, submission);
    setPending(submission);
    inFlight.current = true;
    setError('');
    try {
      const { expected_identity, ...applyRequest } = submission;
      const result = await apply.mutateAsync(applyRequest);
      if (
        Object.entries(expected_identity).some(
          ([key, value]) => result.identity[key as keyof Identity] !== value,
        )
      )
        throw new Error(
          'Save receipt differs from the reviewed candidate. Recover the same request before proceeding.',
        );
      retain(recoveryKey, null);
      if (mounted.current) {
        setPending(null);
        setCreated(result.created_versions);
        setSavedId(result.release_id);
        setPreview(null);
        onSaved?.(result.release_id);
      }
    } catch (cause) {
      const denied = axios.isAxiosError(cause) && cause.response?.status === 403;
      const rejected =
        axios.isAxiosError(cause) &&
        cause.response &&
        cause.response.status >= 400 &&
        cause.response.status < 500;
      if (rejected) retain(recoveryKey, null);
      if (mounted.current) {
        if (rejected) {
          setPending(null);
          setPreview(null);
        }
        if (denied) discardEdit();
        setError(
          getForgeFailureMessage(cause) ||
            getHttpErrorMessage(cause) ||
            'Save outcome is unknown. Recover the same request before preparing another candidate.',
        );
      }
      if (rejected && !denied) {
        void record.refetch();
        if (suite) void members.refetch();
      }
    } finally {
      inFlight.current = false;
    }
  };
  if (savedId)
    return (
      <Stack>
        <Alert color="green">
          Candidate saved.{' '}
          {created?.length
            ? `${created.length} explicitly edited implementation version(s) created.`
            : 'Zero Automation versions created.'}
        </Alert>
        <SavedReleaseWorkflow releaseId={savedId} canEdit={canEdit} />
      </Stack>
    );
  if (releaseId && record.isError)
    return (
      <Alert color="red">
        Selected composition unavailable.{' '}
        <Button onClick={() => void record.refetch()}>Refresh</Button>
      </Alert>
    );
  if (inspection.error || (releaseId && record.data && error && !initialized.current))
    return <Alert color="red">Composition inspection failed: {inspection.error || error}</Alert>;
  if (releaseId && (!record.data || !record.isFetchedAfterMount || !initialized.current))
    return <Text>Loading exact composition…</Text>;
  if (sourceUnavailable && (!source.data || sourceDenied))
    return (
      <Alert color="red">
        Exact source inspection unavailable.{' '}
        <Button disabled={source.isFetching} onClick={() => void source.refetch()}>
          Retry source inspection
        </Button>
      </Alert>
    );
  if (!releaseId && rootVersionId && (!source.data || !source.isFetchedAfterMount))
    return <Text>Loading exact source…</Text>;
  const targetChoices = [
    ...request.roots.map((root) => ({
      location: {
        root: {
          automation_id: root.automation_id,
          automation_version_id: root.automation_version_id,
          trigger_step_id: root.trigger_step_id,
        },
        call_chain: [],
      },
      implementation: root,
    })),
    ...request.initial_selections.map((item) => ({
      location: item.location,
      implementation: item.implementation,
    })),
    ...visibleOccurrences.map((item) => ({
      location: item.location,
      implementation:
        request.selections.find(
          (selection) => locationKey(selection.location) === locationKey(item.location),
        )?.replacement ??
        record.data!.candidate.nodes.find((node) => node.binding_id === item.bindingId)!,
    })),
  ];
  const targets = [
    ...new Map(targetChoices.map((item) => [locationKey(item.location), item])).values(),
  ];
  const hasProposalChanges = Boolean(edit) || replace || proposalChanged(request, proposalBasis);
  return (
    <Stack>
      <Title order={3}>
        {mode === 'guided' ? 'Choose child implementations' : 'Release Builder'}
      </Title>
      <Paper withBorder p="md">
        <Text fw={600}>
          {mode === 'guided'
            ? 'Select exact versions for logical child automations'
            : 'For coordinated automations and advanced quality controls'}
        </Text>
        <Text size="sm" c="dimmed">
          {mode === 'guided'
            ? 'Your draft and all of its triggers are fixed for this Release. No implementation is selected automatically.'
            : 'Standard publishing remains available from the Automation Editor. Use this workspace when you need to select dependencies, coordinate multiple automations, or define deliverable checks.'}
        </Text>
      </Paper>
      <ReleaseWorkflowProgress
        status={record.data?.status}
        isReady={Boolean(readiness.data?.technical_ready)}
        hasBlockers={Boolean(readiness.data?.blockers.length)}
      />
      <details
        open={activeStage === 1}
        onToggle={(event) => {
          if (event.currentTarget.open) setActiveStage(1);
        }}
      >
        <summary>1. Automations and dependencies</summary>
        <ReleaseSection
          title="Automations and dependencies"
          description="Choose the exact automation versions and execution triggers for this release."
        >
          <Group>
            <Text fw={600}>Release selection</Text>
            <Badge color={hasProposalChanges ? 'orange' : 'gray'}>
              {hasProposalChanges ? 'Unsaved changes' : 'Included selections match saved candidate'}
            </Badge>
          </Group>
          <Text size="sm">
            Dependency choices define this release. Editing logic or a public interface creates a
            new draft version. Saving this selection does not run a test or publish it.
          </Text>
          {record.data && hasProposalChanges && (
            <Alert color="orange">
              Tests and publication below use the saved candidate. Save your reviewed changes to
              test them.
            </Alert>
          )}
          {error && <Alert color="red">{error}</Alert>}
          {sourceUnavailable && (
            <Alert color="red">
              Exact source inspection unavailable.{' '}
              <Button disabled={source.isFetching} onClick={() => void source.refetch()}>
                Retry source inspection
              </Button>
            </Alert>
          )}
          {staleBase && (
            <Alert color="orange">
              Saved composition changed. Discard selections and reload its current basis before
              preparing or saving.
            </Alert>
          )}
          {record.data && canEdit && (
            <Button
              variant="subtle"
              disabled={busy || Boolean(pending)}
              onClick={() => {
                const basis = preparationFromRecord(record.data!);
                change(basis);
                setProposalBasis(basis);
                setEdit(undefined);
                setEditVersionId(null);
                setReplace(false);
                void record.refetch();
              }}
            >
              Discard selections and reload saved basis
            </Button>
          )}
          {suite && request.expected_suite_head_revision !== suite.head_revision && (
            <Alert color="orange">
              Suite membership changed. Start a new Suite candidate from the current membership
              before preparing. The saved candidate retains its original membership basis.
            </Alert>
          )}
          {members.isError && <Alert color="red">{(members.error as Error).message}</Alert>}
          {!releaseId &&
            suite &&
            (members.isFetchedAfterMount && !members.isError ? members.data : [])?.map((member) => (
              <Stack key={member.automation_id}>
                <Text fw={600}>
                  {member.name} ·{' '}
                  {member.is_entrypoint ? 'Entrypoint' : 'Independent executable member'}
                </Text>
                <ImplementationSelect
                  automationId={member.automation_id}
                  value={versions[member.automation_id] ?? null}
                  disabled={locked}
                  onChange={(versionId) => {
                    setVersions((current) => ({
                      ...current,
                      [member.automation_id]: versionId ?? '',
                    }));
                    chooseRoots(member.automation_id, []);
                  }}
                />
                {versions[member.automation_id] && (
                  <RootTriggers
                    versionId={versions[member.automation_id]}
                    role={member.is_entrypoint ? 'entrypoint' : 'supporting_member'}
                    selected={request.roots.filter(
                      (root) => root.automation_id === member.automation_id,
                    )}
                    onChange={(roots) => chooseRoots(member.automation_id, roots)}
                    disabled={locked}
                  />
                )}
              </Stack>
            ))}
          {!releaseId && rootVersionId && source.data && mode === 'advanced' && (
            <RootTriggers
              versionId={rootVersionId}
              role="entrypoint"
              selected={request.roots}
              onChange={(roots) => chooseRoots(source.data!.automation_id, roots)}
              disabled={locked}
            />
          )}
          {mode === 'guided' && !request.roots.length && (
            <Alert color="orange">
              This version has no selectable execution triggers. Return to the Editor and refresh
              publishing readiness.
            </Alert>
          )}
          {!releaseId &&
            request.roots.map((root) => (
              <InitialCalls
                key={JSON.stringify(root)}
                sourceVersionId={root.automation_version_id}
                root={root}
                selections={request.initial_selections}
                onSelection={chooseInitial}
                disabled={locked}
              />
            ))}
          {record.data &&
            visibleOccurrences.map((item) => {
              const edge = record.data!.candidate.edges[item.edgeIndex];
              const node = record.data!.candidate.nodes.find(
                (node) => node.binding_id === item.bindingId,
              )!;
              const selection = request.selections.find(
                (selection) => locationKey(selection.location) === locationKey(item.location),
              );
              return (
                <Stack key={locationKey(item.location)} gap="xs">
                  <Text fw={600}>
                    <LocationLabel location={item.location} />
                  </Text>
                  <Text size="sm">
                    Saved selection: <ImplementationLabel version={node} />
                  </Text>
                  <ExactDetails
                    values={{
                      'Binding ID': item.bindingId,
                      'Version ID': node.automation_version_id,
                    }}
                  />
                  {edge.call.binding_mode === 'pinned' ? (
                    <Text size="sm">
                      Historical saved pin. An explicit logic edit is required to convert this call.
                    </Text>
                  ) : (
                    <ImplementationSelect
                      automationId={node.automation_id}
                      value={
                        selection?.replacement.automation_version_id ?? node.automation_version_id
                      }
                      disabled={locked}
                      onChange={(versionId) =>
                        chooseInitial(item.location, node.automation_id, versionId, node)
                      }
                    />
                  )}
                  {selection && (
                    <InitialCalls
                      sourceVersionId={selection.replacement.automation_version_id}
                      root={item.location.root}
                      chain={item.location.call_chain}
                      selections={request.initial_selections}
                      inherited={inherited}
                      replacements={request.selections}
                      onSelection={chooseInitial}
                      disabled={locked}
                    />
                  )}
                </Stack>
              );
            })}
          {canEdit && mode === 'advanced' && (
            <details>
              <summary>Advanced authoring options</summary>
              <Stack pt="sm">
                <Text size="sm" c="dimmed">
                  Edit implementation logic only when this release needs more than exact dependency
                  selection.
                </Text>
                {new Set(request.roots.map((root) => root.automation_version_id)).size > 1 && (
                  <>
                    <Select
                      label="Implementation to edit"
                      value={selectedEditVersion ?? null}
                      data={request.roots.map((root, index) => ({
                        value: root.automation_version_id,
                        label: `Release root ${index + 1}`,
                      }))}
                      disabled={locked || Boolean(edit)}
                      onChange={setEditVersionId}
                    />
                    {selectedEditVersion && (
                      <Text size="sm">
                        Editing:{' '}
                        <ImplementationLabel
                          version={{
                            automation_id: selectedEditRoot?.automation_id ?? '',
                            automation_version_id: selectedEditVersion,
                          }}
                        />
                      </Text>
                    )}
                  </>
                )}
                <ImplementationEditor
                  key={selectedEditVersion}
                  versionId={selectedEditVersion}
                  onEdit={(next) => {
                    if (!next) {
                      discardEdit();
                      return;
                    }
                    setEdit(next);
                    setPreview(null);
                  }}
                  disabled={locked}
                  initialEdit={edit}
                  onLogicalSelection={(path, automationId, versionId, triggerId) => {
                    const choices = request.roots
                      .filter((root) => root.automation_version_id === selectedEditVersion)
                      .map((root) => ({
                        location: {
                          root: {
                            automation_id: root.automation_id,
                            automation_version_id: root.automation_version_id,
                            trigger_step_id: root.trigger_step_id,
                          },
                          call_chain: [path],
                        },
                        implementation: {
                          automation_id: automationId,
                          automation_version_id: versionId,
                        },
                      }));
                    change(
                      choices.reduce(
                        (intent, choice) =>
                          chooseDependency(
                            intent,
                            choice.location,
                            choice.implementation,
                            matchingInheritedCall(inherited.get(locationKey(choice.location)), {
                              binding_mode: 'composition',
                              target_automation_id: automationId,
                              target_trigger_step_id: triggerId,
                            }),
                          ),
                        request,
                      ),
                    );
                  }}
                />
                {edit?.operations
                  .filter((operation) => operation.op_type === 'replace_subautomation_target')
                  .flatMap((operation) =>
                    [
                      ...request.initial_selections,
                      ...request.selections.map((choice) => ({
                        location: choice.location,
                        implementation: choice.replacement,
                      })),
                    ]
                      .filter(
                        (choice) =>
                          choice.location.root.automation_version_id === edit.base_version_id &&
                          choice.location.call_chain.length === 1 &&
                          choice.location.call_chain[0].at(-1)?.step_id === operation.step_id &&
                          (choice.location.call_chain[0].at(-1)?.subflow_id ?? undefined) ===
                            operation.subflow_id,
                      )
                      .map((choice) => (
                        <InitialCalls
                          key={locationKey(choice.location)}
                          sourceVersionId={choice.implementation.automation_version_id}
                          root={choice.location.root}
                          chain={choice.location.call_chain}
                          selections={request.initial_selections}
                          onSelection={chooseInitial}
                          inherited={inherited}
                          replacements={request.selections}
                          disabled={locked}
                        />
                      )),
                  )}
              </Stack>
            </details>
          )}
          <Group justify="flex-end">
            <Button disabled={!request.roots.length} onClick={() => setActiveStage(2)}>
              {mode === 'guided'
                ? 'Continue to implementation review'
                : 'Continue to review selection'}
            </Button>
          </Group>
        </ReleaseSection>
      </details>
      <details
        open={activeStage === 2}
        onToggle={(event) => {
          if (event.currentTarget.open) setActiveStage(2);
        }}
      >
        <summary>2. Review and save the release selection</summary>
        <ReleaseSection
          title={
            mode === 'guided'
              ? '2. Review selected implementations'
              : '2. Versions and deliverables'
          }
          description={
            mode === 'guided'
              ? 'The compiler will verify every selected child version before you can save the Release candidate.'
              : 'Review the selected versions and optionally define exact test evidence for human review.'
          }
        >
          {mode === 'advanced' && !request.requirements.length && (
            <Text size="sm">
              No release-specific test evidence is included. You can still save a valid candidate.
            </Text>
          )}
          {mode === 'advanced' &&
            request.requirements.map((item) => (
              <Group key={item.declaration.requirement_id}>
                <Text>
                  {item.declaration.scope} · {proofKindLabel[item.declaration.kind]} ·{' '}
                  {outcomeLabel[item.declaration.outcome]}
                  {' · '}
                  {item.declaration.required_for_publication
                    ? 'Declared test evidence'
                    : 'Optional evidence'}
                </Text>
                {canEdit && (
                  <Button
                    variant="subtle"
                    disabled={locked}
                    onClick={() =>
                      change({
                        ...request,
                        requirements: request.requirements.filter((other) => other !== item),
                      })
                    }
                  >
                    Remove test evidence
                  </Button>
                )}
              </Group>
            ))}
          {canEdit && mode === 'advanced' && (
            <details>
              <summary>Advanced evidence and candidate options</summary>
              <Stack pt="sm">
                <RequirementEditor
                  targets={targets}
                  existingRequirements={request.requirements}
                  disabled={locked}
                  onAdd={(requirement) => {
                    if (
                      request.requirements.some(
                        (item) =>
                          item.declaration.requirement_id ===
                          requirement.declaration.requirement_id,
                      )
                    ) {
                      setError('Requirement ID already exists.');
                      return false;
                    }
                    change({ ...request, requirements: [...request.requirements, requirement] });
                    return true;
                  }}
                />
                {record.data && (
                  <Checkbox
                    label="Replace this draft release configuration"
                    checked={replace}
                    disabled={locked || record.data.status !== 'draft'}
                    onChange={(event) => {
                      setReplace(event.currentTarget.checked);
                      setPreview(null);
                    }}
                  />
                )}
              </Stack>
            </details>
          )}
          {canEdit && (
            <Button
              disabled={
                locked ||
                !request.roots.length ||
                staleBase ||
                suiteUnavailable ||
                members.isError ||
                Boolean(suite && request.expected_suite_head_revision !== suite.head_revision) ||
                (suite && !releaseId
                  ? !members.data ||
                    members.data.some(
                      (member) =>
                        !request.roots.some((root) => root.automation_id === member.automation_id),
                    )
                  : false)
              }
              onClick={() => void prepareCandidate()}
            >
              {mode === 'guided' ? 'Review selected implementations' : 'Review release selection'}
            </Button>
          )}
          <ReleaseSelectionReview
            preview={preview}
            canSave={Boolean(
              canEdit &&
              !busy &&
              !sourceUnavailable &&
              !staleBase &&
              !suiteUnavailable &&
              preview?.intent.candidate_identity &&
              preview.intent.status === 'ready' &&
              !expired,
            )}
            expired={expired}
            onSave={() => void save()}
            pendingRequestId={pending?.request_id}
            onRecover={() => void save()}
            recoverDisabled={busy || !canEdit || sourceUnavailable}
          />
        </ReleaseSection>
      </details>
      {record.data && (
        <SavedReleaseReview
          record={record.data}
          canEdit={canEdit}
          onChanged={() => void record.refetch()}
        />
      )}
      {!record.data && (
        <Text size="sm">
          Save a reviewed candidate to inspect its evidence, prepare tests and review publication.
        </Text>
      )}
    </Stack>
  );
}
